import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { ChartLineUp, Users, Eye, Globe, DeviceMobile, Desktop, DeviceTablet } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { WeeklyTrafficEmailPanel } from "@/components/WeeklyTrafficEmailPanel";
import { AdminAccessNotice } from "@/components/AdminAccessNotice";

const pct = (cur, prev) => (prev ? Math.round(((cur - prev) / prev) * 100) : null);
const Delta = ({ cur, prev, testid }) => {
  const p = pct(cur, prev);
  if (p === null) return <span className="text-xs text-gray-400 font-sans" data-testid={testid}>no prior week</span>;
  return (
    <span className={`text-xs font-sans font-semibold ${p >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`} data-testid={testid}>
      {p >= 0 ? "▲" : "▼"} {Math.abs(p)}% vs previous 7 days
    </span>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [7, 30, 90];

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-white border border-gray-200 p-5">
    <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider font-sans">
      <Icon size={16} /> {label}
    </div>
    <div className="text-3xl font-serif font-bold text-[#111827] mt-2">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1 font-sans">{sub}</div>}
  </div>
);

const DEVICE_ICONS = { mobile: DeviceMobile, desktop: Desktop, tablet: DeviceTablet };

export default function AdminAnalyticsPage() {
  const { user, token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/analytics/summary?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setData(res.data); setError(""); })
      .catch((e) => setError(e.response?.status === 403 ? "Admin access required" : "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  if (!user || error === "Admin access required") {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Header />
        <AdminAccessNotice user={user} area="the site analytics dashboard" />
        <Footer />
      </div>
    );
  }

  const deviceTotal = data?.devices?.reduce((s, d) => s + d.views, 0) || 0;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="admin-analytics-page">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#111827] pb-4">
          <div>
            <div className="flex items-center gap-2 text-[#2D5A27] text-xs uppercase tracking-widest font-sans font-semibold">
              <ChartLineUp size={16} /> Site Analytics
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-[#111827] mt-1">Visitor Traffic</h1>
            <p className="text-sm text-gray-500 font-sans mt-1">Live first-party analytics — no third-party trackers, bots filtered out.</p>
          </div>
          <div className="flex gap-1" data-testid="analytics-range-selector">
            {RANGES.map((r) => (
              <button
                key={r}
                data-testid={`analytics-range-${r}`}
                onClick={() => setDays(r)}
                className={`px-4 py-2 text-sm font-sans border transition-colors ${days === r ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"}`}
              >
                {r} days
              </button>
            ))}
          </div>
        </div>

        {error && <div className="mt-8 text-red-600 font-sans" data-testid="analytics-error">{error}</div>}
        {loading && !data && <div className="mt-8 text-gray-500 font-sans">Loading analytics…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors.toLocaleString()} sub={`last ${data.range_days} days`} testid="stat-unique-visitors" />
              <StatCard icon={Eye} label="Page Views" value={data.total_views.toLocaleString()} sub={`last ${data.range_days} days`} testid="stat-page-views" />
              <StatCard icon={Users} label="Visitors Today" value={data.today_visitors.toLocaleString()} testid="stat-today-visitors" />
              <StatCard icon={Eye} label="Views Today" value={data.today_views.toLocaleString()} testid="stat-today-views" />
            </div>

            {data.all_time && data.week_compare && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <div className="bg-white border border-gray-200 p-5" data-testid="stat-since-launch">
                  <div className="text-gray-500 text-xs uppercase tracking-wider font-sans">Since launch{data.all_time.since ? ` · counting from ${data.all_time.since}` : ""}</div>
                  <div className="flex gap-8 mt-2">
                    <div><div className="text-3xl font-serif font-bold text-[#111827]" data-testid="since-launch-visitors">{data.all_time.visitors.toLocaleString()}</div><div className="text-xs text-gray-400 font-sans">unique visitors</div></div>
                    <div><div className="text-3xl font-serif font-bold text-[#111827]" data-testid="since-launch-views">{data.all_time.views.toLocaleString()}</div><div className="text-xs text-gray-400 font-sans">page views</div></div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 p-5" data-testid="stat-week-compare">
                  <div className="text-gray-500 text-xs uppercase tracking-wider font-sans">Last 7 days vs previous 7 days</div>
                  <div className="flex gap-8 mt-2">
                    <div>
                      <div className="text-3xl font-serif font-bold text-[#111827]" data-testid="week-visitors">{data.week_compare.current.visitors.toLocaleString()}</div>
                      <div className="text-xs text-gray-400 font-sans">visitors (prev {data.week_compare.previous.visitors.toLocaleString()})</div>
                      <Delta cur={data.week_compare.current.visitors} prev={data.week_compare.previous.visitors} testid="week-visitors-delta" />
                    </div>
                    <div>
                      <div className="text-3xl font-serif font-bold text-[#111827]" data-testid="week-views">{data.week_compare.current.views.toLocaleString()}</div>
                      <div className="text-xs text-gray-400 font-sans">views (prev {data.week_compare.previous.views.toLocaleString()})</div>
                      <Delta cur={data.week_compare.current.views} prev={data.week_compare.previous.views} testid="week-views-delta" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 p-5 mt-6" data-testid="analytics-daily-chart">
              <h2 className="text-lg font-serif font-bold text-[#111827] mb-4">Daily Traffic</h2>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.daily} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2D5A27" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#2D5A27" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="views" name="Page views" stroke="#2D5A27" fill="url(#viewsGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#B45309" fill="none" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <div className="bg-white border border-gray-200 p-5" data-testid="analytics-top-pages">
                <h2 className="text-lg font-serif font-bold text-[#111827] mb-3">Top Pages</h2>
                {data.top_pages.length === 0 ? (
                  <p className="text-sm text-gray-400 font-sans">No page views recorded yet.</p>
                ) : (
                  <table className="w-full text-sm font-sans">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-200">
                        <th className="py-2">Page</th>
                        <th className="py-2 text-right">Views</th>
                        <th className="py-2 text-right">Visitors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_pages.map((p) => (
                        <tr key={p.path} className="border-b border-gray-100">
                          <td className="py-2 text-[#111827] truncate max-w-[220px]">{p.path}</td>
                          <td className="py-2 text-right">{p.views.toLocaleString()}</td>
                          <td className="py-2 text-right text-gray-500">{p.visitors.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-gray-200 p-5" data-testid="analytics-referrers">
                  <h2 className="text-lg font-serif font-bold text-[#111827] mb-3 flex items-center gap-2"><Globe size={18} /> Traffic Sources</h2>
                  {data.top_referrers.length === 0 ? (
                    <p className="text-sm text-gray-400 font-sans">No external referrers yet — direct visits only.</p>
                  ) : (
                    <ul className="space-y-2">
                      {data.top_referrers.map((r) => (
                        <li key={r.referrer} className="flex justify-between text-sm font-sans border-b border-gray-100 pb-2">
                          <span className="text-[#111827]">{r.referrer}</span>
                          <span className="text-gray-500">{r.views.toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="bg-white border border-gray-200 p-5" data-testid="analytics-devices">
                  <h2 className="text-lg font-serif font-bold text-[#111827] mb-3">Devices</h2>
                  {deviceTotal === 0 ? (
                    <p className="text-sm text-gray-400 font-sans">No data yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {data.devices.map((d) => {
                        const Icon = DEVICE_ICONS[d.device] || Desktop;
                        const pct = Math.round((d.views / deviceTotal) * 100);
                        return (
                          <div key={d.device}>
                            <div className="flex justify-between text-sm font-sans mb-1">
                              <span className="flex items-center gap-1.5 capitalize text-[#111827]"><Icon size={16} /> {d.device}</span>
                              <span className="text-gray-500">{pct}%</span>
                            </div>
                            <div className="h-2 bg-gray-100">
                              <div className="h-2 bg-[#2D5A27]" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <WeeklyTrafficEmailPanel token={token} email={user.email} />
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
